"use client";

import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useQueryState } from "nuqs";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { StateVariant } from "@/components/types";
import { PRODUCT_TYPE, ProductType } from "@/constants";
import { useActiveAccounts } from "@/hooks/useActiveAccounts";
import { useAutoAccountSwitch } from "@/hooks/useAutoAccountSwitch";
import useBaseCurrencyConverter from "@/hooks/useBaseCurrencyConverter";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useGetBeneficiaryByType } from "@/hooks/useGetBeneficiaryByType";
import { reportSilentFailure } from "@/lib/reportSilentFailure";
import { useCustomerAccount } from "@/providers/CustomerAccountProvider";
import {
  createSwapInstruction,
  shouldReportEmptyReceiveCurrency,
  type SwapInstructionContext,
} from "@/providers/PricingProvider/createSwapInstruction";
import {
  buildCreatePricingEngineRequest,
  type CurrencyRelation,
  getCurrencyRelation,
  getIsSameCurrency,
  handleApiError,
} from "@/providers/PricingProvider/helpers";
import {
  createInitialState,
  type ExchangeState,
  pricingReducer,
} from "@/providers/PricingProvider/pricingReducer";
import { usePricingPolling } from "@/providers/PricingProvider/usePricingPolling";
import { usePricingStatus } from "@/providers/PricingProvider/usePricingStatus";
import { useTicker } from "@/providers/TickerProvider";
import { useAppDispatch } from "@/redux/hooks";
import {
  determineCurrency as determineCurrencyUtil,
  findAlternativeCurrency as findAlternativeCurrencyUtil,
  getBeneficiaryCurrency,
} from "@/utils/currency";
import { FlowStep } from "@/utils/flowStep";
import { classifyMutationFailure } from "@/utils/mutationOutcome";
import {
  api,
  type GetPricingRequestApiResponse,
  type PricingSwapInstruction,
  useAcceptPricingRequestMutation,
  useCreatePricingRequestMutation,
  useRequotePricingRequestMutation,
} from "@generated/redux/services/generated/api";
import {
  FilteredBeneficiaryDetail,
  FilteredPricingEngineRequest,
  FilteredResolvedBlockchainBeneficiary,
  PricingQuoteState,
  QuoteAmountType,
  TenorType,
} from "@generated/types";

export type ConfirmExchangeOptions = {
  /**
   * Takes over an accept that ended with no definite answer (no response, a
   * timeout, any 5xx: `classifyMutationFailure` in `utils/mutationOutcome.ts`
   * says "unknown"). The quote may have been booked, so the provider sets no
   * error message and still resolves `false`; the caller re-reads the pricing
   * request and calls `reportFailure` (which sets the usual message) only if
   * the accept did not take. Without this option every failure sets the
   * message, as before.
   */
  onOutcomeUnknown?: (failure: {
    error: unknown;
    reportFailure: () => void;
  }) => void;
};

export interface PricingContextValue {
  exchangeState: ExchangeState;
  updateSellAmount: (amount: number, isUserInput?: boolean) => void;
  updateBuyAmount: (amount: number, isUserInput?: boolean) => void;
  setSellCurrency: (currency: string) => void;
  setBuyCurrency: (currency: string) => void;
  createPricingRequest: () => Promise<FilteredPricingEngineRequest | undefined>;
  getPricingQuote: (
    isDebouncedQuote?: boolean,
    currentSellAmount?: number,
    currentBuyAmount?: number,
    lastEditedSide?: "sell" | "buy"
  ) => Promise<FilteredPricingEngineRequest | undefined>;
  getMaxAmountQuote: (
    maxAmount: number
  ) => Promise<FilteredPricingEngineRequest | undefined>;
  /**
   * Accepts the quote. `true` when it was accepted, `false` on any failure;
   * `options.onOutcomeUnknown` tells an unknown failure apart.
   */
  confirmExchange: (options?: ConfirmExchangeOptions) => Promise<boolean>;
  resetExchange: () => void;
  fetchPricingRequestById: (
    id: string
  ) => Promise<FilteredPricingEngineRequest>;
  isLoading: boolean;
  error: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pricingRequestCustomError: any;
  /**
   * The pricing-request read has come back — so a null `pricingRequestResponse`
   * means "nothing to show", not "still loading". An empty upstream body is
   * resolved by RTK Query as `data: null` with NO error, so this is the only
   * way a consumer can tell that apart from a slow load (UI-175).
   */
  isPricingRequestSettled: boolean;
  /**
   * The success page stopped polling this pricing request after its cap
   * without a final state, so the status shown may be out of date. Separate
   * from `isPricingRequestSettled` on purpose: stopping is not settling, and
   * it is not an error.
   */
  hasStoppedPolling: boolean;
  /** "Check again": read the pricing request now and resume polling. */
  restartPolling: () => void;
  exchangeRate: number;
  pricingRequestResponse: FilteredPricingEngineRequest | null;
  isIndicativeState: boolean;
  isTradeableState: boolean;
  beneficiary:
    | FilteredBeneficiaryDetail
    | FilteredResolvedBlockchainBeneficiary
    | undefined;
  isBeneficiaryLoading: boolean;
  remittanceInformation: string | undefined;
  purposeCode: string | undefined;
  setRemittanceInformation: (remittanceInformation: string) => void;
  setPurposeCode: (purposeCode: string) => void;
  isPaymentFlow: boolean;
  /**
   * How the loaded instruction's currencies relate, with "not yet resolved"
   * kept apart from "same" (`getCurrencyRelation`). Read this wherever a
   * screen must not treat a missing pair as a same-currency payment: the
   * review and the receipt.
   */
  currencyRelation: CurrencyRelation;
  /**
   * Same-currency flag for the consumers written before `currencyRelation`
   * (`getIsSameCurrency`). True when both instruction currencies are present
   * and equal, or while the instruction carries neither (no quote yet); false
   * when they differ or only one is present. So `TradeFinalityBanner` stays
   * hidden on the pay review before the first quote and the amount-entry Max
   * button keeps its same-currency branch until then. Do not read it to tell
   * "unresolved" from "same": use `currencyRelation`.
   */
  isSameCurrency: boolean;
  isBlockchainPayment: boolean;
  areCurrenciesResolved: boolean;
  isTradeScheduled: boolean;
  isPaymentScheduled: boolean;
  isTransactionScheduled: boolean;
  isPaymentCancelled: boolean;
  animatedStateCircle: StateVariant;
  identifier: string | undefined;
}

/**
 * Exported for unit tests only, which render a screen against a context value
 * they build themselves. Going through the real provider would let its effects
 * correct `exchangeState` one render after the first commit, hiding anything a
 * screen gets wrong while that draft is still at its reducer defaults. App
 * code reads the context through `usePricing()`; the barrel does not export it.
 */
export const PricingContext = createContext<PricingContextValue | undefined>(
  undefined
);

interface PricingProviderProps {
  children: React.ReactNode;
  /**
   * The flow step, read by the mounting flow layout from its own segments
   * (`getFlowStep(useSelectedLayoutSegments())`). Drives which pricing-request
   * reads run: none on amount, a read on review, polling on success.
   */
  flowStep: FlowStep;
}

export function PricingProvider({ children, flowStep }: PricingProviderProps) {
  const { type, beneficiaryId, pricingRequestId } = useParams<{
    type: ProductType;
    beneficiaryId: string;
    pricingRequestId: string;
  }>();
  const tCustomError = useTranslations("exchange.customErrorHandling");
  const [initialSellCurrency] = useQueryState("sell");
  const [initialBuyCurrency] = useQueryState("buy");

  const reduxDispatch = useAppDispatch();
  const { customerAccount, customerAccountBaseCurrency } = useCustomerAccount();
  const { baseCurrency } = useTicker();
  const { convert } = useBaseCurrencyConverter();
  const {
    accounts: activeAccountsList,
    availableAccounts: availableAccountsList,
  } = useActiveAccounts();

  // Get tradable currencies from capabilities for fallback when active accounts don't have alternatives
  const { tradable, payable } = useCapabilities();

  // Convert arrays to maps for quick lookup - memoized to prevent unnecessary re-renders
  const activeAccounts = useMemo(
    () => Object.fromEntries(activeAccountsList.map((a) => [a.code, a])),
    [activeAccountsList]
  );
  const availableAccounts = useMemo(
    () => Object.fromEntries(availableAccountsList.map((a) => [a.code, a])),
    [availableAccountsList]
  );

  // The customer's known-currency universe — every code the backend reports as
  // tradable or payable. Passed into the currency utils so a URL param that
  // isn't a real currency (e.g. `?sell=XYZ`) is rejected instead of echoed back
  // (which would otherwise re-seed the URL with garbage). See `utils/currency.ts`.
  const knownCurrencyCodes = useMemo(
    () =>
      new Set<string>([
        ...tradable.map((t) => t.id),
        ...payable.map((p) => p.id),
      ]),
    [tradable, payable]
  );

  const [pricingRequestResponse, setPricingRequestResponse] =
    useState<FilteredPricingEngineRequest | null>(null);

  const [remittanceInformation, setRemittanceInformation] = useState<
    string | undefined
  >();
  const [purposeCode, setPurposeCode] = useState<string | undefined>(undefined);

  const [state, dispatch] = useReducer(
    pricingReducer,
    {
      sellCurrency: initialSellCurrency || "",
      buyCurrency: initialBuyCurrency || "",
      pricingRequestId,
    },
    createInitialState
  );
  const { exchange: exchangeState, error } = state;
  const { shouldPollOnSuccess, areCurrenciesResolved } = state;

  // Ref to latest exchange state — allows stable useCallback functions to
  // read current values without listing exchangeState as a dependency
  const exchangeStateRef = useRef(exchangeState);
  exchangeStateRef.current = exchangeState;

  // Ref to the latest `areCurrenciesResolved` so `buildSwapInstruction`'s
  // watchdog can gate on it WITHOUT adding it to the useCallback deps —
  // widening those deps would give buildSwapInstruction a fresh identity every
  // time the flag flips and cascade through getPricingQuote → effects (the
  // identity chain in render-stability.md / pricing-provider-stability.md).
  const areCurrenciesResolvedRef = useRef(areCurrenciesResolved);
  areCurrenciesResolvedRef.current = areCurrenciesResolved;

  // Track if we're restoring from navigation
  const [isRestoringFromNav] = useState(() => {
    const isNavigatingBack =
      sessionStorage.getItem("navigatingBack") === "true";
    return isNavigatingBack;
  });

  const { data: beneficiary, isLoading: isBeneficiaryLoading } =
    useGetBeneficiaryByType(type, beneficiaryId);

  const isPaymentFlow = !!beneficiaryId;

  // Memoize beneficiary currency to avoid callback instability
  // This ensures findAlternativeCurrency doesn't get new references when beneficiary object changes but currency stays the same
  const beneficiaryCurrency = useMemo(
    () =>
      getBeneficiaryCurrency(
        beneficiary as
          | FilteredBeneficiaryDetail
          | FilteredResolvedBlockchainBeneficiary
          | undefined
      ),
    [beneficiary]
  );

  // Dispatch helpers — thin wrappers passed to callbacks that expect (value) => void
  const setCustomError = useCallback(
    (message: string) => dispatch({ type: "SET_ERROR", message }),
    []
  );

  // Upsert mutation results into RTK Query cache so polling picks them up seamlessly.
  // Also updates local state for step 1 where the polling query is skipped.
  const updatePricingResponse = useCallback(
    (result: FilteredPricingEngineRequest | null) => {
      setPricingRequestResponse(result);
      if (result?.id) {
        reduxDispatch(
          api.util.upsertQueryData(
            "getPricingRequest",
            { uuid: result.id },
            result as unknown as GetPricingRequestApiResponse
          )
        );
      }
    },
    [reduxDispatch]
  );

  // Reset currencies resolved flag when beneficiaryId changes
  useEffect(() => {
    if (beneficiaryId) {
      dispatch({ type: "SET_CURRENCIES_RESOLVED", value: false });
    }
  }, [beneficiaryId]);

  // Mutations
  const [createPricingRequestMutation, { isLoading: createLoading }] =
    useCreatePricingRequestMutation();
  const [requotePricingRequestMutation, { isLoading: requoteLoading }] =
    useRequotePricingRequestMutation();
  const [acceptPricingRequestMutation, { isLoading: acceptLoading }] =
    useAcceptPricingRequestMutation();

  const isFirstStep = flowStep === FlowStep.Amount;
  const isSuccessPage = flowStep === FlowStep.Success;

  // Polling lifecycle — extracted to usePricingPolling hook
  const onStopPolling = useCallback(
    () => dispatch({ type: "SET_SHOULD_POLL", value: false }),
    []
  );
  const onResetPolling = useCallback(
    () => dispatch({ type: "SET_SHOULD_POLL", value: true }),
    []
  );
  const {
    pricingRequestData,
    pricingRequestCustomError,
    isPricingRequestSettled,
    hasStoppedPolling,
    restartPolling,
    fetchPricingRequestById,
  } = usePricingPolling({
    pricingRequestId,
    isFirstStep,
    isSuccessPage,
    shouldPollOnSuccess,
    onStopPolling,
    onResetPolling,
  });

  // Auto-switch account if pricing request belongs to a different customer account.
  // Called here (not in layout) to reuse pricingRequestData from usePricingPolling
  // and avoid a duplicate useGetPricingRequestQuery subscription.
  useAutoAccountSwitch(
    pricingRequestId,
    isFirstStep,
    pricingRequestData as FilteredPricingEngineRequest | undefined
  );

  // Helper function to get available currencies
  const getAvailableCurrencies = useCallback(() => {
    if (!activeAccounts) return { sortedActiveAccountKeys: [] };

    // Get all account keys (no type filtering - cross-asset payments are now allowed)
    const accountKeys = Object.keys(activeAccounts);

    // Sort purely by base currency value (highest first), without base currency priority
    const sortedActiveAccountKeys = accountKeys.sort((a, b) => {
      const accountA = activeAccounts[a];
      const accountB = activeAccounts[b];

      if (!accountA || !accountB) return 0;

      // Convert both balances to base currency for comparison
      const valueA = convert(accountA.balance, a);
      const valueB = convert(accountB.balance, b);

      // Sort by value descending (highest first)
      return valueB - valueA;
    });

    return {
      sortedActiveAccountKeys,
    };
  }, [activeAccounts, convert]);

  // Helper function to find alternative currency
  // Passes beneficiary currency to allow same-currency payments to bypass canTrade check
  const findAlternativeCurrency = useCallback(
    (excludeCurrency: string) => {
      const { sortedActiveAccountKeys } = getAvailableCurrencies();
      return findAlternativeCurrencyUtil(excludeCurrency, {
        sortedActiveAccountKeys,
        baseCurrency,
        activeAccounts,
        availableAccounts,
        beneficiaryCurrency,
        knownCurrencyCodes,
      });
    },
    [
      getAvailableCurrencies,
      baseCurrency,
      activeAccounts,
      availableAccounts,
      beneficiaryCurrency,
      knownCurrencyCodes,
    ]
  );

  // Main helper function to determine currency with proper priority
  const determineCurrency = useCallback(
    (
      preferredCurrency?: string,
      excludeCurrency?: string,
      useBeneficiaryCurrency?: boolean
    ) => {
      const { sortedActiveAccountKeys } = getAvailableCurrencies();
      return determineCurrencyUtil(
        preferredCurrency,
        excludeCurrency,
        useBeneficiaryCurrency,
        {
          sortedActiveAccountKeys,
          baseCurrency,
          customerAccountBaseCurrency,
          beneficiary: beneficiary as
            | FilteredBeneficiaryDetail
            | FilteredResolvedBlockchainBeneficiary
            | undefined,
          activeAccounts,
          availableAccounts,
          knownCurrencyCodes,
        }
      );
    },
    [
      getAvailableCurrencies,
      customerAccountBaseCurrency,
      baseCurrency,
      activeAccounts,
      availableAccounts,
      beneficiary,
      knownCurrencyCodes,
    ]
  );

  // Helper function to get first tradable currency from capabilities as a fallback
  // This is used when determineCurrency() can't find a suitable currency from active accounts
  const getFirstTradableCurrency = useCallback(
    (excludeCurrency?: string): string => {
      const firstTradable = tradable.find(
        (t) => t.isTradable && t.id !== excludeCurrency
      );
      return firstTradable?.id || "";
    },
    [tradable]
  );

  // Enhanced version of findAlternativeCurrency that falls back to capabilities
  const findAlternativeCurrencyWithFallback = useCallback(
    (excludeCurrency: string) => {
      const alternative = findAlternativeCurrency(excludeCurrency);
      if (alternative) return alternative;
      // Fallback to tradable currencies from capabilities
      return getFirstTradableCurrency(excludeCurrency);
    },
    [findAlternativeCurrency, getFirstTradableCurrency]
  );

  // Polled data takes priority (stays fresh via cache + polling).
  // Local state is the fallback for step 1 where the polling query is skipped.
  // Memoized so the context value and `swapInstructionCtx` don't get a new
  // identity on every render — cascading through `buildSwapInstruction` and
  // `getPricingQuote` would re-fire effects that depend on them.
  const sourceData = useMemo(
    () =>
      (pricingRequestData as unknown as
        | FilteredPricingEngineRequest
        | undefined) || pricingRequestResponse,
    [pricingRequestData, pricingRequestResponse]
  );

  // Derive display-level status (scheduled, cancelled, exchange rate, etc.)
  const {
    isTradeScheduled,
    isPaymentScheduled,
    isTransactionScheduled,
    isPaymentCancelled,
    animatedStateCircle,
    identifier,
    exchangeRate,
  } = usePricingStatus({
    sourceData,
    isPaymentFlow,
  });

  // Memoized context for the pure createSwapInstruction function.
  // Uses primitive exchangeState fields instead of the whole object so that
  // SET_PRICING_REQUEST_ID dispatches (which only touch pricingRequestId)
  // don't cascade through buildSwapInstruction → getPricingQuote.
  const swapInstructionCtx: SwapInstructionContext = useMemo(
    () => ({
      exchangeState: {
        sellCurrency: exchangeState.sellCurrency,
        sellAmount: exchangeState.sellAmount,
        buyCurrency: exchangeState.buyCurrency,
        buyAmount: exchangeState.buyAmount,
        lastEditedSide: exchangeState.lastEditedSide,
        quoteType: exchangeState.quoteType,
      },
      sourceData: sourceData ?? null,
      remittanceInformation,
      purposeCode,
      beneficiary: beneficiary as
        | FilteredBeneficiaryDetail
        | FilteredResolvedBlockchainBeneficiary
        | undefined,
      type,
      isPaymentFlow,
    }),
    [
      exchangeState.sellCurrency,
      exchangeState.sellAmount,
      exchangeState.buyCurrency,
      exchangeState.buyAmount,
      exchangeState.lastEditedSide,
      exchangeState.quoteType,
      sourceData,
      remittanceInformation,
      purposeCode,
      beneficiary,
      type,
      isPaymentFlow,
    ]
  );

  // Thin wrapper — delegates to the pure extracted function
  const buildSwapInstruction = useCallback(
    (
      partialSwapInstruction?: Partial<PricingSwapInstruction>,
      overrideRemittanceInformation?: string,
      overridePurposeCode?: string
    ): PricingSwapInstruction | undefined => {
      const swapInstruction = createSwapInstruction(
        swapInstructionCtx,
        partialSwapInstruction,
        overrideRemittanceInformation,
        overridePurposeCode
      );
      // Every caller treats undefined as "silently do nothing" — the user
      // types an amount on /pay and no quote ever appears, with no organic
      // error anywhere. Report it (reporting lives here, not in the pure
      // createSwapInstruction, to keep that function side-effect-free).
      //
      // Gate on `areCurrenciesResolved` (read via ref — see its declaration):
      // before currencies resolve, the user is mid-entry on /pay (typing a
      // sell amount before the receive currency has been selected/resolved) and
      // an empty instruction is the EXPECTED transient state, not a stuck flow.
      // Reporting then produced false positives (CUSTOMER-UI-6N). Only a
      // resolved-yet-still-empty instruction is a genuine silent failure.
      if (
        shouldReportEmptyReceiveCurrency({
          swapInstruction,
          isPaymentFlow: swapInstructionCtx.isPaymentFlow,
          areCurrenciesResolved: areCurrenciesResolvedRef.current,
        })
      ) {
        reportSilentFailure({
          message:
            "[Pricing] Swap instruction blocked — empty receive currency in payment flow",
          fingerprint: "pricing-empty-receive-currency",
        });
      }
      return swapInstruction;
    },
    [swapInstructionCtx]
  );

  const updateSellAmount = useCallback(
    (amount: number, isUserInput: boolean = true) => {
      dispatch({ type: "UPDATE_SELL_AMOUNT", amount, isUserInput });
    },
    []
  );

  const updateBuyAmount = useCallback(
    (amount: number, isUserInput: boolean = true) => {
      dispatch({ type: "UPDATE_BUY_AMOUNT", amount, isUserInput });
    },
    []
  );

  // Shared API operation: requote existing request or create a new one.
  // Extracted to eliminate ~80% code duplication between getPricingQuote and getMaxAmountQuote.
  const executePricingOperation = useCallback(
    async (
      swapInstruction: PricingSwapInstruction
    ): Promise<FilteredPricingEngineRequest> => {
      const currentState = exchangeStateRef.current;
      // The BFF derives the originator from the signed-in user on create and
      // requote alike (and the operation type on requote), so neither body
      // carries identity.
      if (currentState.pricingRequestId) {
        return (await requotePricingRequestMutation({
          uuid: currentState.pricingRequestId,
          requotePricingRequest: {
            updates: [swapInstruction as unknown as PricingSwapInstruction],
          },
        }).unwrap()) as unknown as FilteredPricingEngineRequest;
      }
      return (await createPricingRequestMutation({
        createPricingEngineRequest: buildCreatePricingEngineRequest({
          customerAccountId: customerAccount!.customerAccount!.id as string,
          instruction: swapInstruction as unknown as PricingSwapInstruction,
        }),
      }).unwrap()) as unknown as FilteredPricingEngineRequest;
    },
    [
      requotePricingRequestMutation,
      createPricingRequestMutation,
      customerAccount,
    ]
  );

  // Helper function to handle pricing request response
  const handlePricingRequestResponse = useCallback(
    (
      result: FilteredPricingEngineRequest,
      isDebouncedQuote: boolean = false
    ) => {
      const currentExchange = exchangeStateRef.current;
      const isTradeableState = result.state === PricingQuoteState.Tradeable;

      updatePricingResponse(result);

      // Only update pricingRequestId in exchange state, don't update amounts/currencies
      // to prevent infinite loops during user input
      dispatch({ type: "SET_PRICING_REQUEST_ID", id: result.id });

      // If this is a debounced quote (not from continue button), update the other input
      if (isDebouncedQuote && result.instructions?.[0]) {
        const instruction = result.instructions[0];
        if (
          currentExchange.lastEditedSide === "sell" &&
          instruction.receiveAmount &&
          instruction.receiveAmount !== currentExchange.buyAmount
        ) {
          updateBuyAmount(instruction.receiveAmount, false);
        } else if (
          currentExchange.lastEditedSide === "buy" &&
          instruction.payAmount !== undefined &&
          instruction.payAmount !== currentExchange.sellAmount
        ) {
          updateSellAmount(instruction.payAmount, false);
        }
      }

      // Persist state for back-navigation restoration (consumers handle the actual navigation)
      if (isTradeableState && result.id && !isDebouncedQuote) {
        try {
          const instr = result.instructions?.[0];
          const stateToSave = {
            ...currentExchange,
            sellCurrency: instr?.payCurrency || currentExchange.sellCurrency,
            buyCurrency: instr?.receiveCurrency || currentExchange.buyCurrency,
            sellAmount: instr?.payAmount ?? currentExchange.sellAmount,
            buyAmount: instr?.receiveAmount ?? currentExchange.buyAmount,
            pricingRequestId: result.id,
            remittanceInformation,
            purposeCode,
          };
          sessionStorage.setItem("exchangeState", JSON.stringify(stateToSave));
          sessionStorage.setItem("navigatingBack", "true");
        } catch (e) {
          console.warn("Failed to persist exchange state", e);
        }
      }
    },
    [
      updateBuyAmount,
      updateSellAmount,
      updatePricingResponse,
      remittanceInformation,
      purposeCode,
    ]
  );

  const setSellCurrency = useCallback(
    (currency: string) => {
      dispatch({
        type: "SET_SELL_CURRENCY",
        currency,
        findAlternative: findAlternativeCurrencyWithFallback,
        hasBeneficiary: !!beneficiary,
      });
    },
    [findAlternativeCurrencyWithFallback, beneficiary]
  );

  const setBuyCurrency = useCallback(
    (currency: string) => {
      dispatch({
        type: "SET_BUY_CURRENCY",
        currency,
        findAlternative: findAlternativeCurrencyWithFallback,
      });
    },
    [findAlternativeCurrencyWithFallback]
  );

  const createPricingRequest = useCallback(async () => {
    try {
      const swapInstruction = buildSwapInstruction();
      if (!swapInstruction) return undefined;

      const pricingRequest = await executePricingOperation(swapInstruction);
      updatePricingResponse(pricingRequest);
      handlePricingRequestResponse(pricingRequest);
      return pricingRequest;
    } catch (err) {
      const currentState = exchangeStateRef.current;
      handleApiError(
        err,
        tCustomError("FAILED_TO_CREATE_PRICING_REQUEST"),
        setCustomError,
        tCustomError,
        currentState.sellCurrency,
        currentState.buyCurrency
      );
      return undefined;
    }
  }, [
    buildSwapInstruction,
    executePricingOperation,
    handlePricingRequestResponse,
    updatePricingResponse,
    setCustomError,
    tCustomError,
  ]);

  const confirmExchange = useCallback(
    async (options?: ConfirmExchangeOptions) => {
      const currentState = exchangeStateRef.current;
      if (!currentState.pricingRequestId) {
        setCustomError(tCustomError("NO_PRICING_REQUEST_AVAILABLE"));
        return false;
      }

      try {
        // No body at all — the BFF derives the ACCEPT operation and the
        // originator from the authenticated session (UI-172).
        await acceptPricingRequestMutation({
          uuid: currentState.pricingRequestId,
        }).unwrap();

        return true;
      } catch (err) {
        const reportFailure = () =>
          handleApiError(
            err,
            tCustomError("FAILED_TO_EXECUTE_EXCHANGE"),
            setCustomError,
            tCustomError,
            currentState.sellCurrency,
            currentState.buyCurrency
          );
        if (
          options?.onOutcomeUnknown &&
          classifyMutationFailure(err) === "unknown"
        ) {
          options.onOutcomeUnknown({ error: err, reportFailure });
        } else {
          reportFailure();
        }
        return false;
      }
    },
    [acceptPricingRequestMutation, setCustomError, tCustomError]
  );

  const getPricingQuote = useCallback(
    async (
      isDebouncedQuote: boolean = false,
      currentSellAmount?: number,
      currentBuyAmount?: number,
      lastEditedSide?: "sell" | "buy",
      overrideRemittanceInformation?: string,
      overridePurposeCode?: string
    ) => {
      try {
        dispatch({ type: "CLEAR_ERROR" });
        const currentState = exchangeStateRef.current;

        const sellAmount = currentSellAmount ?? currentState.sellAmount;
        const buyAmount = currentBuyAmount ?? currentState.buyAmount;
        // Use the explicitly passed lastEditedSide to avoid race conditions with state updates
        const editedSide = lastEditedSide ?? currentState.lastEditedSide;
        // Calculate the correct quote type based on which side was edited
        const quoteType =
          editedSide === "sell"
            ? QuoteAmountType.ReceiveAmount
            : QuoteAmountType.PayAmount;

        // Create swap instruction with current values
        const swapInstruction = buildSwapInstruction(
          {
            payCurrency: currentState.sellCurrency,
            payAmount: editedSide === "sell" ? sellAmount : undefined,
            receiveCurrency: currentState.buyCurrency,
            receiveAmount: editedSide === "buy" ? buyAmount : undefined,
            quote: quoteType,
          },
          overrideRemittanceInformation,
          overridePurposeCode
        );

        if (!swapInstruction) return undefined;

        const result = await executePricingOperation(swapInstruction);
        handlePricingRequestResponse(result, isDebouncedQuote);
        return result;
      } catch (err) {
        const currentState = exchangeStateRef.current;
        handleApiError(
          err,
          tCustomError("FAILED_TO_CREATE_PRICING_REQUEST"),
          setCustomError,
          tCustomError,
          currentState.sellCurrency,
          currentState.buyCurrency
        );
        return undefined;
      }
    },
    [
      buildSwapInstruction,
      executePricingOperation,
      handlePricingRequestResponse,
      setCustomError,
      tCustomError,
    ]
  );

  const getMaxAmountQuote = useCallback(
    async (maxAmount: number) => {
      try {
        dispatch({ type: "CLEAR_ERROR" });
        const currentState = exchangeStateRef.current;

        // Create swap instruction specifically for max amount (sell all, receive whatever)
        const swapInstruction = buildSwapInstruction({
          payCurrency: currentState.sellCurrency,
          payAmount: maxAmount,
          receiveCurrency: currentState.buyCurrency,
          receiveAmount: undefined,
          quote: QuoteAmountType.ReceiveAmount,
          tenor: TenorType.Instant,
        });

        if (!swapInstruction) return undefined;

        const result = await executePricingOperation(swapInstruction);
        updatePricingResponse(result);
        dispatch({ type: "SET_PRICING_REQUEST_ID", id: result.id });

        // For max amount quotes, always update the buy amount with the received amount
        if (result.instructions?.[0]?.receiveAmount) {
          updateBuyAmount(result.instructions[0].receiveAmount, false);
        }

        return result;
      } catch (err) {
        const currentState = exchangeStateRef.current;
        handleApiError(
          err,
          tCustomError("FAILED_TO_CREATE_PRICING_REQUEST"),
          setCustomError,
          tCustomError,
          currentState.sellCurrency,
          currentState.buyCurrency
        );
        return undefined;
      }
    },
    [
      buildSwapInstruction,
      executePricingOperation,
      updatePricingResponse,
      setCustomError,
      tCustomError,
      updateBuyAmount,
    ]
  );

  const resetExchange = useCallback(() => {
    const sellCurr = determineCurrency(initialSellCurrency ?? undefined);
    const buyCurr = determineCurrency(
      initialBuyCurrency ?? undefined,
      sellCurr,
      isPaymentFlow
    );

    dispatch({
      type: "RESET",
      sellCurrency: sellCurr || "",
      buyCurrency: buyCurr || "",
    });

    setPricingRequestResponse(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [determineCurrency, beneficiary, initialSellCurrency, initialBuyCurrency]);

  // Sync exchange state from fetched/polled pricing request data.
  // Only on step 2+ — on step 1 the user is actively editing currencies,
  // and stale cached data from a previous request must not overwrite them.
  useEffect(() => {
    if (sourceData && !isFirstStep) {
      const instruction = sourceData.instructions?.[0];

      if (instruction) {
        // Skip dispatch if state already has this pricing request's data synced.
        // Without this guard, every sourceData reference change (polling, GET responses)
        // creates a new exchangeState object via spread, which triggers the currency
        // change detection in exchange-amount-entry and fires an unnecessary REQUOTE.
        const current = exchangeStateRef.current;
        const alreadySynced =
          current.pricingRequestId === sourceData.id &&
          current.sellCurrency === instruction.payCurrency &&
          current.buyCurrency === instruction.receiveCurrency;

        if (!alreadySynced) {
          dispatch({
            type: "SYNC_FROM_SOURCE",
            instruction: {
              payCurrency: instruction.payCurrency,
              receiveCurrency: instruction.receiveCurrency,
              payAmount: instruction.payAmount,
              receiveAmount: instruction.receiveAmount,
            },
            sourceId: sourceData.id,
          });
        }
      }
    }
  }, [sourceData, isFirstStep]);

  // No BOOKED-state side effects needed here. Balances and ledger entries
  // refresh naturally when the user navigates back to dashboard/accounts
  // (queries re-fetch on mount). The success page handles its own balance
  // refetch via useBalances().refetch() in its useEffect.

  // Restore previously saved exchange state on mount and optionally re-quote
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem("exchangeState");
      let restoredSell = exchangeState.sellAmount;
      let restoredBuy = exchangeState.buyAmount;
      let restoredRemittanceInfo: string | undefined;
      let restoredPurposeCode: string | undefined;
      let restoredLastEditedSide: "sell" | "buy" | undefined;

      if (saved) {
        const parsed = JSON.parse(saved);
        dispatch({ type: "RESTORE_FROM_SESSION", parsed });
        restoredSell = parsed.sellAmount ?? restoredSell;
        restoredBuy = parsed.buyAmount ?? restoredBuy;
        restoredLastEditedSide = parsed.lastEditedSide;

        if (
          Object.prototype.hasOwnProperty.call(parsed, "remittanceInformation")
        ) {
          // A snapshot null means "not known", so it becomes undefined (the
          // state's own type): createSwapInstruction then omits the key, which
          // keeps the stored value on a requote, rather than sending a clear.
          restoredRemittanceInfo = parsed.remittanceInformation ?? undefined;
          setRemittanceInformation(restoredRemittanceInfo);
        }
        if (Object.prototype.hasOwnProperty.call(parsed, "purposeCode")) {
          restoredPurposeCode = parsed.purposeCode ?? undefined;
          setPurposeCode(restoredPurposeCode);
        }
      }
      // Clear saved snapshot to avoid stale restores later
      sessionStorage.removeItem("exchangeState");
      sessionStorage.removeItem("navigatingBack");

      // If we navigated back and have amounts, trigger a debounced re-quote once
      // Pass remittanceInformation and purposeCode explicitly to avoid stale closure issues
      if (
        isRestoringFromNav &&
        !isSuccessPage &&
        (restoredSell > 0 || restoredBuy > 0)
      ) {
        const timer = setTimeout(() => {
          setPricingRequestResponse(null);
          getPricingQuote(
            true,
            restoredSell,
            restoredBuy,
            restoredLastEditedSide,
            restoredRemittanceInfo,
            restoredPurposeCode
          );
        }, 500);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      console.warn("Failed to restore exchange state", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Once currencies are resolved, this URL→state seed must STOP running.
    // From that point on the URL-sync writer in `use-exchange-amount-entry.ts`
    // owns the `?sell`/`?buy` params (its effects are gated on
    // `areCurrenciesResolved`). Those writes are fanned back into
    // `initialSellCurrency`/`initialBuyCurrency` by nuqs's synchronous
    // cross-hook emitter (both are deps below), so without this guard the
    // writer's own URL echo re-runs this effect → re-dispatches SET_CURRENCIES
    // → mutates exchangeState → re-fires the writer: the feedback loop behind
    // React #185 (Sentry CUSTOMER-UI-3S). This guard and the writer's
    // `!areCurrenciesResolved` gate are complementary — this pulls URL→state
    // only BEFORE resolution; the writer pushes state→URL only AFTER — so the
    // two can never form a cycle. Re-seeding still happens on a genuine reset
    // (RESET / beneficiary change clears `areCurrenciesResolved`). See
    // `.claude/docs/incident-log.md` and
    // `.claude/rules/web/domains/pricing-provider-stability.md`.
    if (
      areCurrenciesResolved &&
      exchangeState.sellCurrency &&
      exchangeState.buyCurrency
    ) {
      return;
    }

    const { sortedActiveAccountKeys } = getAvailableCurrencies();

    // Don't update currencies if we're in the middle of an exchange flow
    if (
      exchangeState.pricingRequestId ||
      exchangeState.sellAmount > 0 ||
      exchangeState.buyAmount > 0
    ) {
      return;
    }

    // Wait for accounts to load
    if (sortedActiveAccountKeys.length === 0) return;

    // Wait for beneficiary to load in payment flows before setting currencies
    if (isPaymentFlow && isBeneficiaryLoading) {
      return;
    }

    let newSellCurr;
    if (initialSellCurrency) {
      newSellCurr = determineCurrency(initialSellCurrency);
    } else if (isPaymentFlow) {
      newSellCurr = determineCurrency(undefined, undefined, true);
    } else {
      newSellCurr = determineCurrency(undefined);
    }
    newSellCurr = newSellCurr || exchangeState.sellCurrency;

    let newBuyCurr = determineCurrency(
      initialBuyCurrency ?? undefined,
      newSellCurr,
      isPaymentFlow
    );

    if (!newBuyCurr && !isPaymentFlow) {
      newBuyCurr = getFirstTradableCurrency(newSellCurr);
    }

    newBuyCurr = newBuyCurr || exchangeState.buyCurrency;

    // SET_CURRENCIES is a no-op if values haven't changed (handled in reducer).
    // `findAlternative`/`hasBeneficiary` let the reducer break a sell===buy
    // collision in exchange flow while allowing same-currency payments.
    dispatch({
      type: "SET_CURRENCIES",
      sellCurrency: newSellCurr,
      buyCurrency: newBuyCurr,
      findAlternative: findAlternativeCurrencyWithFallback,
      hasBeneficiary: !!beneficiary,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeAccounts,
    availableAccounts,
    initialSellCurrency,
    initialBuyCurrency,
    beneficiary,
    beneficiaryId,
    isBeneficiaryLoading,
    tradable,
    // Engages the resolved-gate above the moment resolution flips, so the
    // URL→state seed stops before the writer takes ownership of the URL.
    areCurrenciesResolved,
  ]);

  useEffect(() => {
    // Don't overwrite user-entered values when restoring from navigation
    if (isRestoringFromNav) {
      return;
    }
    if (beneficiary) {
      const defaults = (beneficiary as FilteredBeneficiaryDetail).defaults;
      setRemittanceInformation(defaults?.remittanceInformation);
      setPurposeCode(defaults?.purposeCode);
    }
  }, [beneficiary, isRestoringFromNav]);

  // Track when currencies are actually resolved in the state
  useEffect(() => {
    // Don't mark as resolved if we're restoring from navigation with amounts
    if (
      isRestoringFromNav &&
      (exchangeState.sellAmount > 0 || exchangeState.buyAmount > 0)
    ) {
      return;
    }

    // Check if currencies are set
    const hasValidCurrencies =
      exchangeState.sellCurrency && exchangeState.buyCurrency;

    if (!hasValidCurrencies) {
      dispatch({ type: "SET_CURRENCIES_RESOLVED", value: false });
      return;
    }

    // In payment flows, ensure beneficiary is loaded and matches current URL
    if (beneficiaryId) {
      // Still loading beneficiary - don't resolve
      if (isBeneficiaryLoading) {
        dispatch({ type: "SET_CURRENCIES_RESOLVED", value: false });
        return;
      }

      // No beneficiary data yet OR ID mismatch - don't resolve
      if (!beneficiary || beneficiary.id !== beneficiaryId) {
        dispatch({ type: "SET_CURRENCIES_RESOLVED", value: false });
        return;
      }

      // Verify buy currency matches beneficiary's currency
      const expectedCurrency =
        beneficiary.resourceType === "beneficiaries"
          ? (beneficiary as FilteredBeneficiaryDetail).currency
          : (beneficiary as FilteredResolvedBlockchainBeneficiary).asset
              ?.ledgerProduct;

      if (expectedCurrency && exchangeState.buyCurrency === expectedCurrency) {
        dispatch({ type: "SET_CURRENCIES_RESOLVED", value: true });
      } else if (!expectedCurrency) {
        // Beneficiary loaded but no expected currency - still resolve
        dispatch({ type: "SET_CURRENCIES_RESOLVED", value: true });
      } else {
        dispatch({ type: "SET_CURRENCIES_RESOLVED", value: false });
      }
    } else {
      // Exchange flow - currencies just need to be set
      dispatch({ type: "SET_CURRENCIES_RESOLVED", value: true });
    }
  }, [
    exchangeState.sellCurrency,
    exchangeState.buyCurrency,
    beneficiaryId,
    beneficiary,
    isBeneficiaryLoading,
    isRestoringFromNav,
    exchangeState.sellAmount,
    exchangeState.buyAmount,
  ]);

  const value: PricingContextValue = useMemo(() => {
    const instruction = sourceData?.instructions?.[0];
    const currencyRelation = getCurrencyRelation(instruction);
    return {
      exchangeState,
      updateSellAmount,
      updateBuyAmount,
      setSellCurrency,
      setBuyCurrency,
      createPricingRequest,
      getPricingQuote,
      getMaxAmountQuote,
      confirmExchange,
      resetExchange,
      fetchPricingRequestById,
      isLoading:
        createLoading ||
        requoteLoading ||
        acceptLoading ||
        isBeneficiaryLoading,
      error,
      pricingRequestCustomError,
      isPricingRequestSettled,
      hasStoppedPolling,
      restartPolling,
      exchangeRate,
      pricingRequestResponse: sourceData ?? null,
      isIndicativeState: sourceData?.state === PricingQuoteState.Indicative,
      isTradeableState: sourceData?.state === PricingQuoteState.Tradeable,
      beneficiary: beneficiary as
        | FilteredBeneficiaryDetail
        | FilteredResolvedBlockchainBeneficiary
        | undefined,
      isBeneficiaryLoading,
      purposeCode,
      setPurposeCode,
      remittanceInformation,
      setRemittanceInformation,
      isPaymentFlow,
      currencyRelation,
      isSameCurrency: getIsSameCurrency(instruction),
      isBlockchainPayment: type === PRODUCT_TYPE.DIGITAL_ASSETS,
      areCurrenciesResolved,
      isTradeScheduled,
      isPaymentScheduled,
      isTransactionScheduled,
      isPaymentCancelled,
      animatedStateCircle,
      identifier,
    };
  }, [
    exchangeState,
    updateSellAmount,
    updateBuyAmount,
    setSellCurrency,
    setBuyCurrency,
    createPricingRequest,
    getPricingQuote,
    getMaxAmountQuote,
    confirmExchange,
    resetExchange,
    fetchPricingRequestById,
    createLoading,
    requoteLoading,
    acceptLoading,
    isBeneficiaryLoading,
    error,
    pricingRequestCustomError,
    isPricingRequestSettled,
    hasStoppedPolling,
    restartPolling,
    exchangeRate,
    sourceData,
    beneficiary,
    purposeCode,
    setPurposeCode,
    remittanceInformation,
    setRemittanceInformation,
    isPaymentFlow,
    type,
    areCurrenciesResolved,
    isTradeScheduled,
    isPaymentScheduled,
    isTransactionScheduled,
    isPaymentCancelled,
    animatedStateCircle,
    identifier,
  ]);

  return (
    <PricingContext.Provider value={value}>{children}</PricingContext.Provider>
  );
}

export function usePricing() {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error("usePricing must be used within a PricingProvider");
  }
  return context;
}
